import { FC, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { createLoadOptions } from "../../hooks/useDebounce";
import { useI18n } from "../../hooks/useTranslations";
import { CarCategory } from "../../http/cars/types";
import { useSubsidiariesApi } from "../../http/subsidiaries/api";
import { Subsidiary } from "../../http/subsidiaries/types";
import { SubsidiaryVatCode } from "../../http/subsidiaryVatCodes/types";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import Input from "../ui/Input";
import reactSelectStyles from "../ui/ReactSelectUtils";

const SUBSIDIARIES_FIELDS = ["id", "name"] as const;
type SubsidiaryField = (typeof SUBSIDIARIES_FIELDS)[number];
type SubsidiarySearch = Pick<Subsidiary, SubsidiaryField>;

export interface SubsidiaryVatCodeFormBody {
  id: string;
  subsidiary: Subsidiary;
  carCategory: CarCategory;
  productCode: string;
  vatCode: string;
}

type SubsidiaryVatCodeFormProps = {
  subVatCode?: SubsidiaryVatCode | null;
  onSubmit: (form: SubsidiaryVatCodeFormBody) => Promise<void>;
  isDisabled?: boolean;
  isLoading?: boolean;
  isTab?: boolean;
};

const SubsidiaryVatCodeForm: FC<SubsidiaryVatCodeFormProps> = ({
  subVatCode,
  onSubmit,
  isDisabled,
  isLoading,
  isTab,
}: SubsidiaryVatCodeFormProps) => {
  const i18n = useI18n();
  const subsidiariesApi = useSubsidiariesApi();

  const [defaultSubsidiaryOptions, setDefaultSubsidiaryOptions] = useState<SubsidiarySearch[]>([]);

  const categories: CarCategory[] = [
    { value: "pkw", label: i18n.t.common.pkw },
    { value: "lkw", label: i18n.t.common.lkw },
    { value: "pkw-superior", label: i18n.t.common.pkwSuperior },
  ];

  const defaultValues = {
    subsidiary: subVatCode?.subsidiary,
    carCategory: categories.find((x) => x.value === subVatCode?.carCategory),
    productCode: subVatCode?.productCode,
    vatCode: subVatCode?.vatCode,
  };

  return (
    <Form<SubsidiaryVatCodeFormBody>
      defaultValues={defaultValues}
      submitText={i18n.t.common.save}
      disabled={isDisabled}
      onSubmit={() => onSubmit}
      itemCreationInfo={{
        id: subVatCode?.id,
        author: subVatCode?.author,
        createdAt: subVatCode?.createdAt,
        editor: subVatCode?.editor,
        modifiedAt: subVatCode?.modifiedAt,
        versionNumber: subVatCode?.versionNumber,
      }}
    >
      {({ register, formState: { errors }, control, reset }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [subVatCode]);

        return (
          <FormFieldsContainer>
            {!isTab && (
              <Controller
                name="subsidiary"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.subsidiaries.singular} error={fieldState.error}>
                    {({ labelId, isInvalid }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef(new AbortController());

                      const loadOptions = createLoadOptions<SubsidiarySearch, SubsidiaryField>(
                        aborter,
                        searchTerm,
                        subsidiariesApi.getAdminSubsidiaries,
                        { fields: SUBSIDIARIES_FIELDS },
                      );

                      useEffect(() => {
                        loadOptions(searchTerm.current).then(setDefaultSubsidiaryOptions);
                      }, []);

                      return (
                        <AsyncSelect
                          menuPosition="fixed"
                          {...field}
                          inputId={labelId}
                          isDisabled={isDisabled || isLoading}
                          isLoading={isLoading}
                          defaultOptions={defaultSubsidiaryOptions}
                          loadOptions={loadOptions}
                          placeholder={`${i18n.t.common.select}...`}
                          getOptionLabel={(x) => x.name}
                          getOptionValue={(x) => x.id}
                          styles={reactSelectStyles<SubsidiarySearch>(isInvalid)}
                        />
                      );
                    }}
                  </FormField>
                )}
              />
            )}
            <Controller
              name="carCategory"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.common.category} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Select
                      menuPosition="fixed"
                      {...field}
                      inputId={labelId}
                      isDisabled={isDisabled || isLoading}
                      isLoading={isLoading}
                      options={categories}
                      placeholder={`${i18n.t.common.select}...`}
                      getOptionLabel={(x) => x.label}
                      getOptionValue={(x) => x.value}
                      styles={reactSelectStyles<CarCategory>(isInvalid)}
                    />
                  )}
                </FormField>
              )}
            />
            <FormField label={i18n.t.common.productCode} error={errors.productCode}>
              {({ labelId, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isDisabled || isLoading}
                  {...register("productCode", {
                    required: true,
                  })}
                />
              )}
            </FormField>
            <FormField label={i18n.t.common.vatCode} error={errors.vatCode}>
              {({ labelId, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isDisabled || isLoading}
                  {...register("vatCode", {
                    required: true,
                  })}
                />
              )}
            </FormField>
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default SubsidiaryVatCodeForm;
